import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { PhoneCall, BarChart3, ListMusic, Star, ChevronDown } from 'lucide-react';

const TABS = [
  // El listado va primero porque es lo que se viene a hacer aquí casi siempre:
  // buscar una llamada concreta. Los agregados son la consulta ocasional.
  { to: '/llamadas', label: 'Llamadas', icon: ListMusic, end: true },
  { to: '/llamadas/metricas', label: 'Métricas', icon: BarChart3, end: false },
  { to: '/llamadas/guardadas', label: 'Guardadas', icon: Star, end: false },
];

/** First year with recorded calls; the pipeline started in December 2022. */
const PRIMER_ANIO = 2022;

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function hoyMadrid(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
}

function haceDias(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
}

/** Days in a month, so February and the 31-day months show the right buttons. */
function diasDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
}

const dos = (n: number) => String(n).padStart(2, '0');

/** Shared by both tabs through the outlet, so switching view keeps the period. */
export interface LlamadasContext {
  from: string;
  to: string;
}

type Periodo = { preset: string | null; anio: number | null; mes: number | null; dia: number | null };

/** Lo que se lee en la cabecera cuando el selector está plegado. */
function resumenPeriodo(v: Periodo): string {
  if (v.preset) return v.preset;
  if (v.anio === null) return '90 días';
  if (v.mes === null) return String(v.anio);
  if (v.dia === null) return `${MESES[v.mes]} ${v.anio}`;
  return `${v.dia} ${MESES[v.mes]} ${v.anio}`;
}

/**
 * Period picker.
 *
 * Two ways of asking the same question, because they answer different ones. The
 * relative windows are for "what is going on lately". The calendar drill-down —
 * year, then month, then day — is for looking something up: a rolling 365-day
 * window mixed two summers together, and August has fifteen times the calls of
 * January, so comparing like with like needs real calendar boundaries.
 *
 * En móvil se pliega tras un botón. Desplegado ocupa casi una pantalla entera
 * —cuatro años, doce meses y treinta y un días—, y dejarlo siempre visible
 * empujaba el listado por debajo del pliegue, que es justo lo que se venía a ver.
 */
function SelectorPeriodo({ valor, onChange }: { valor: Periodo; onChange: (v: Periodo) => void }) {
  const [abierto, setAbierto] = useState(false);
  const anioActual = Number(hoyMadrid().slice(0, 4));
  const anios: number[] = [];
  for (let a = anioActual; a >= PRIMER_ANIO; a--) anios.push(a);

  // min-h-9 y px-3 dan un objetivo táctil cómodo sin agrandar el diseño de escritorio.
  const boton = (activo: boolean) =>
    `min-h-9 px-3 text-sm rounded-md whitespace-nowrap transition-colors ${
      activo
        ? 'bg-sereno-dark text-white'
        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 active:bg-slate-200 dark:active:bg-white/10'
    }`;

  return (
    <div className="w-full sm:w-auto">
      {/* En móvil, un botón que dice qué periodo hay puesto y despliega el resto. */}
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        className="sm:hidden w-full flex items-center justify-between gap-2 min-h-11 px-3 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-700 dark:text-slate-200"
      >
        <span className="font-medium">{resumenPeriodo(valor)}</span>
        <ChevronDown size={16} className={`transition-transform ${abierto ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`${abierto ? 'flex' : 'hidden'} sm:flex flex-col gap-1.5 mt-2 sm:mt-0 items-stretch sm:items-end`}
      >
        <div className="flex flex-wrap gap-1 justify-start sm:justify-end">
          {['30 días', '90 días'].map((p) => (
            <button
              key={p}
              className={boton(valor.preset === p)}
              onClick={() => onChange({ preset: p, anio: null, mes: null, dia: null })}
            >
              {p}
            </button>
          ))}
          <span className="hidden sm:block w-px bg-slate-200 dark:bg-white/10 mx-1 self-stretch" />
          {anios.map((a) => (
            <button
              key={a}
              className={boton(valor.anio === a)}
              onClick={() => onChange({ preset: null, anio: a, mes: null, dia: null })}
            >
              {a}
            </button>
          ))}
          <button
            className={boton(valor.preset === 'Todo')}
            onClick={() => onChange({ preset: 'Todo', anio: null, mes: null, dia: null })}
          >
            Todo
          </button>
        </div>

        {valor.anio !== null && (
          // Rejilla de cuatro columnas en móvil: doce meses en tres filas ordenadas
          // se recorren mejor que una fila que se parte por donde quepa.
          <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-1 sm:justify-end">
            <button
              className={`${boton(valor.mes === null)} col-span-4 sm:col-auto`}
              onClick={() => onChange({ ...valor, mes: null, dia: null })}
            >
              Todo {valor.anio}
            </button>
            {MESES.map((m, i) => (
              <button key={m} className={boton(valor.mes === i)} onClick={() => onChange({ ...valor, mes: i, dia: null })}>
                {m}
              </button>
            ))}
          </div>
        )}

        {valor.anio !== null && valor.mes !== null && (
          // Siete columnas: los días caen en la misma posición que en un calendario.
          <div className="grid grid-cols-7 sm:flex sm:flex-wrap gap-1 sm:justify-end sm:max-w-2xl">
            <button
              className={`${boton(valor.dia === null)} col-span-7 sm:col-auto`}
              onClick={() => onChange({ ...valor, dia: null })}
            >
              Todo {MESES[valor.mes]}
            </button>
            {Array.from({ length: diasDelMes(valor.anio, valor.mes) }, (_, i) => i + 1).map((d) => (
              <button
                key={d}
                className={`${boton(valor.dia === d)} px-0 sm:px-2 tabular-nums`}
                onClick={() => onChange({ ...valor, dia: d })}
              >
                {d}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Turns the picker's state into the date range the API expects. */
function aRango(v: Periodo) {
  if (v.preset === '30 días') return { from: haceDias(29), to: hoyMadrid() };
  if (v.preset === '90 días') return { from: haceDias(89), to: hoyMadrid() };
  if (v.preset === 'Todo') return { from: `${PRIMER_ANIO}-01-01`, to: hoyMadrid() };
  if (v.anio === null) return { from: haceDias(89), to: hoyMadrid() };
  if (v.mes === null) return { from: `${v.anio}-01-01`, to: `${v.anio}-12-31` };
  const mes = dos(v.mes + 1);
  if (v.dia === null) {
    return { from: `${v.anio}-${mes}-01`, to: `${v.anio}-${mes}-${dos(diasDelMes(v.anio, v.mes))}` };
  }
  const dia = `${v.anio}-${mes}-${dos(v.dia)}`;
  return { from: dia, to: dia };
}

/**
 * Calls to the switchboard, recorded by the Amazon Connect pipeline and read
 * from sereno_shared/calls. One sidebar entry with tabs, same shape as
 * Aperturas, because both answer questions about the same period.
 */
export default function LlamadasLayout() {
  const [periodo, setPeriodo] = useState<Periodo>({ preset: '90 días', anio: null, mes: null, dia: null });
  const rango = aRango(periodo);

  // Las guardadas se listan todas, sin filtrar por fechas: se guarda una llamada
  // para volver a ella, y que desapareciera por el rango elegido sería justo lo
  // contrario. Enseñar ahí un selector que no hace nada confunde, así que no se enseña.
  const sinPeriodo = useLocation().pathname.startsWith('/llamadas/guardadas');

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-sereno-dark p-2 sm:p-2.5 rounded-xl text-white shrink-0">
            <PhoneCall size={20} className="sm:hidden" />
            <PhoneCall size={22} className="hidden sm:block" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-sereno-dark">Llamadas</h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
              Cuándo llama la gente a la centralita, cuánto duran esas llamadas y qué se dice en ellas.
            </p>
          </div>
        </div>
        {!sinPeriodo && <SelectorPeriodo valor={periodo} onChange={setPeriodo} />}
      </div>

      <div className="border-b border-slate-200 dark:border-white/10">
        {/* -mx-4 px-4 en móvil: las pestañas pueden desplazarse de borde a borde
            en vez de quedar recortadas por el margen de la página. */}
        <nav className="flex gap-1 -mb-px overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
          {TABS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 sm:px-4 py-3 sm:py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-sereno-dark text-sereno-dark font-medium dark:border-white dark:text-white'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet context={{ from: rango.from, to: rango.to } satisfies LlamadasContext} />
    </div>
  );
}
